/* eslint-disable dialog/no-unguarded-dialog-dismissal -- Durable broadcast jobs and their control actions persist server-side; closing this status view must never trap the host or cancel work. */
/**
 * Whole-house broadcast delivery tracking:
 *  - BroadcastStatusDialog — every recipient's state (queued / handed to
 *    PadSplit / confirmed in thread / failed), the roster check it was sent
 *    under, and the one-click "send to a missed member" action.
 *  - RecentBroadcastsDialog — recent broadcasts with a delivery roll-up.
 * "Handed to PadSplit" is deliberately never styled as delivered: only a
 * harvested thread copy (or a provider receipt) counts as confirmed.
 */

import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage, queryClient } from "@/lib/queryClient";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Checkbox } from "@/components/ui/checkbox";
import { boundedBroadcastRequest } from "@/hooks/use-broadcast-request";
import { AlertTriangle, Loader2, Send } from "lucide-react";
import {
  type BroadcastRecipientState,
  type RosterCheckResult,
} from "@shared/broadcast-roster";

// Accept the durable states before/after the shared union is deployed.
type RecipientState = BroadcastRecipientState | "sending" | "timed_out" | "unknown";
interface BroadcastJob {
  state: string;
  error?: string | null;
  updatedAt: string;
  deadlineAt: string | null;
}

interface RecipientStatus {
  memberId: number;
  memberName: string;
  propertyId: number;
  pendingId: number;
  channel: "padsplit" | "quo" | "direct";
  source: "broadcast" | "roster_gap" | "auto_roster_gap";
  state: RecipientState;
  sentAt: string | null;
  sendError: string | null;
  attempts: number;
  updatedAt?: string | null;
  startedAt?: string | null;
  confirmedAt?: string | null;
}

interface BroadcastStatusResponse {
  id: string;
  createdAt: string;
  scheduledFor: string | null;
  messageText: string;
  properties: Array<{ id: number; name: string }>;
  rosterCheck: RosterCheckResult | null;
  acknowledgedAt: string | null;
  skipped: Array<{ memberId: number; memberName: string; error: string }>;
  recipients: RecipientStatus[];
  sendTo: { memberId: number; memberName: string; propertyId: number | null; alreadySent: boolean; deselected?: boolean; eligible: boolean } | null;
  job?: BroadcastJob | null;
}

interface RecentBroadcast {
  id: string;
  createdAt: string;
  scheduledFor: string | null;
  propertyNames: string[];
  messagePreview: string;
  recipientCount: number;
  skippedCount: number;
  gapCount: number;
  acknowledged: boolean;
  counts: Partial<Record<RecipientState, number>>;
  job?: BroadcastJob | null;
}

const STATE_STYLES: Record<RecipientState, string> = {
  queued: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
  handed_off: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200",
  confirmed: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200",
  failed: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200",
  cancelled: "bg-muted text-muted-foreground",
  sending: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200",
  timed_out: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
  unknown: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
};
const STATE_LABELS: Record<RecipientState, string> = {
  queued: "Queued",
  sending: "Sending to PadSplit",
  handed_off: "Accepted, not confirmed",
  confirmed: "Sent (confirmed)",
  failed: "Failed",
  timed_out: "Timed out",
  unknown: "Delivery unknown",
  cancelled: "Cancelled",
};
const JOB_LABELS: Record<string, string> = {
  queued: "Queued — waiting to send",
  roster_checking: "Queued — checking residents",
  sending: "Sending to PadSplit",
  sent: "Sent",
  completed: "Sent",
  partial: "Partially sent",
  partially_sent: "Partially sent",
  failed: "Failed",
  timed_out: "Timed out",
  unknown: "Delivery unknown",
  cancelled: "Cancelled",
};
function useStatusClock(open: boolean) {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    if (!open) return;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(timer);
  }, [open]);
  return now;
}
export function broadcastIsActive(item: { job?: BroadcastJob | null; counts?: Partial<Record<RecipientState, number>>; recipients?: RecipientStatus[] }) {
  if (item.job) return ["queued", "sending", "roster_checking", "retrying"].includes(item.job.state);
  return item.recipients
    ? item.recipients.some(r => ["queued", "sending", "handed_off"].includes(r.state))
    : ["queued", "sending", "handed_off"].some(state => (item.counts?.[state as RecipientState] ?? 0) > 0);
}

export function shouldPollBroadcast(item: { job?: BroadcastJob | null; scheduledFor?: string | null; counts?: Partial<Record<RecipientState, number>>; recipients?: RecipientStatus[] }, openedAt: number, now = Date.now()) {
  if (!broadcastIsActive(item) || now - openedAt >= 90_000) return false;
  if (item.scheduledFor && Date.parse(item.scheduledFor) > now) return false;
  return !item.job?.deadlineAt || Date.parse(item.job.deadlineAt) > now;
}

function StateBadge({ state }: { state: RecipientState }) {
  return (
    <span className={`text-xs rounded px-1.5 py-0.5 font-medium whitespace-nowrap ${STATE_STYLES[state]}`} data-testid={`badge-recipient-state-${state}`}>
      {STATE_LABELS[state] ?? "Delivery unknown"}
    </span>
  );
}

const statusKey = (id: string, sendTo: number | null) => [`/api/app/messages/broadcast/${id}/status`, sendTo ?? null];

export function normalizeBroadcastRecipientState(state: string): RecipientState {
  if (state === "pending") return "queued";
  if (state === "sent") return "confirmed";
  return Object.hasOwn(STATE_LABELS, state) ? state as RecipientState : "unknown";
}

export function BroadcastStatusDialog({
  broadcastId,
  sendToMemberId = null,
  onClose,
  onReviewRoster,
}: {
  broadcastId: string | null;
  sendToMemberId?: number | null;
  onClose: () => void;
  onReviewRoster?: () => void;
}) {
  const { toast } = useToast();
  const open = broadcastId != null;
  const [openedAt, setOpenedAt] = useState(Date.now);
  useEffect(() => setOpenedAt(Date.now()), [broadcastId]);
  const [confirmUnknown, setConfirmUnknown] = useState(false);
  useEffect(() => setConfirmUnknown(false), [broadcastId]);
  const query = useQuery<BroadcastStatusResponse>({
    queryKey: statusKey(broadcastId ?? "", sendToMemberId),
    queryFn: async ({ signal }) => {
      const qs = sendToMemberId != null ? `?sendTo=${sendToMemberId}` : "";
      const controller = new AbortController();
      const abort = () => controller.abort();
      signal.addEventListener("abort", abort, { once: true });
      try {
        return await boundedBroadcastRequest(controller, async () => {
          const res = await apiRequest("GET", `/api/app/messages/broadcast/${encodeURIComponent(broadcastId!)}/status${qs}`, undefined, { signal: controller.signal });
          const body = await res.json();
          return {
            ...body,
            recipients: body.recipients.map((r: RecipientStatus) => ({
              ...r,
              state: normalizeBroadcastRecipientState(r.state),
            })),
          };
        }, 12_000);
      } finally {
        signal.removeEventListener("abort", abort);
      }
    },
    enabled: open,
    refetchInterval: q => open && q.state.status !== "error" && q.state.data && shouldPollBroadcast(q.state.data, openedAt) ? 2_000 : false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    retry: false,
  });
  function refreshStatus() {
    void queryClient.invalidateQueries({ queryKey: statusKey(broadcastId ?? "", sendToMemberId) });
    void queryClient.invalidateQueries({ queryKey: ["/api/app/messages/broadcasts/recent"] });
    void queryClient.invalidateQueries({ queryKey: ["/api/app/inbox"] });
  }
  const jobMutation = useMutation({
    retry: false,
    mutationFn: async (action: "retry" | "cancel") => {
      const controller = new AbortController();
      await boundedBroadcastRequest(controller, async () => {
        await apiRequest("POST", `/api/app/messages/broadcast/${encodeURIComponent(broadcastId!)}/${action}`,
          action === "retry" ? { confirmUnknown } : {}, { signal: controller.signal });
      });
      return action;
    },
    onSuccess: (action) => {
      setConfirmUnknown(false);
      setOpenedAt(Date.now());
      refreshStatus();
      toast({
        title: action === "retry" ? "Retry queued — not yet confirmed sent" : "Cancellation requested",
        description: action === "retry"
          ? "Only eligible, unconfirmed recipients are retried. Confirmed messages are never resent."
          : "Pending recipients will be stopped. Messages already sent remain sent; in-flight delivery may be unknown.",
      });
    },
    onError: (err) => {
      refreshStatus();
      toast({ title: "Action not confirmed", description: parseErrorMessage(err, "Refresh delivery status before trying again."), variant: "destructive" });
    },
  });
  const sendMutation = useMutation({
    mutationFn: async (memberId: number) => {
      const res = await apiRequest("POST", `/api/app/messages/broadcast/${encodeURIComponent(broadcastId!)}/send-to-member`, { memberId });
      return res.json() as Promise<{ memberName: string }>;
    },
    onSuccess: (data) => {
      toast({ title: `Broadcast queued for ${data.memberName}`, description: "Not yet confirmed sent. Check delivery status." });
      queryClient.invalidateQueries({ queryKey: [`/api/app/messages/broadcast/${broadcastId}/status`] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/messages/broadcasts/recent"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/inbox"] });
    },
    onError: (err) => {
      toast({ title: "Couldn't send to this member", description: parseErrorMessage(err, "Please try again."), variant: "destructive" });
    },
  });

  const data = query.data;
  const counts: Record<RecipientState, number> = { queued: 0, sending: 0, handed_off: 0, confirmed: 0, failed: 0, timed_out: 0, unknown: 0, cancelled: 0 };
  for (const r of data?.recipients ?? []) counts[r.state] = (counts[r.state] ?? 0) + 1;
  const unknownCount = counts.unknown + counts.handed_off;
  const retryable = counts.failed + counts.timed_out + unknownCount;
  const now = useStatusClock(open && !!data && shouldPollBroadcast(data, openedAt));
  const jobActive = !!data && broadcastIsActive(data);
  const deadlineOverdue = jobActive && !!data?.job?.deadlineAt && now >= new Date(data.job.deadlineAt).getTime();
  const rosterBlocked = !!data?.job && counts.confirmed === 0
    && ["failed", "timed_out"].includes(data.job.state)
    && (data.recipients.every((r) => r.attempts === 0)
      || (!!data.rosterCheck?.gapCount && !data.acknowledgedAt));
  const propName = (id: number) => data?.properties.find((p) => p.id === id)?.name
    ?? (id != null ? `Property #${id}` : data?.properties.length === 1 ? data.properties[0].name : "Property not recorded");

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] overflow-y-auto"
        data-testid="dialog-broadcast-status"
      >
        <DialogHeader>
          <DialogTitle>Broadcast delivery status</DialogTitle>
          <DialogDescription>
            Only confirmed delivery counts as sent. You may close or navigate away; persisted jobs continue in the background. Reopen them in Recent broadcasts.
          </DialogDescription>
        </DialogHeader>
        {query.isError && (
          <div className="text-sm text-red-700" data-testid="broadcast-status-error">
            Status could not be refreshed. Delivery is not confirmed. {parseErrorMessage(query.error, "Couldn't load this broadcast.")}
            <Button size="sm" variant="outline" onClick={() => { setOpenedAt(Date.now()); void query.refetch(); }}>Refresh status</Button>
          </div>
        )}
        {!data ? (!query.isError && (
          <div className="space-y-2 animate-pulse" role="status" aria-label="Loading broadcast"><div className="h-5 w-2/3 rounded bg-muted" /><div className="h-16 rounded bg-muted" /></div>
        )) : (
          <div className="space-y-4">
            <div className="text-sm space-y-1">
              <div className="text-muted-foreground">
                {data.scheduledFor ? `Scheduled for ${new Date(data.scheduledFor).toLocaleString()}` : `Created ${new Date(data.createdAt).toLocaleString()}`}
                {" · "}{data.properties.map((p) => p.name).join(", ")}
              </div>
              <div className="whitespace-pre-wrap rounded bg-muted/50 p-2 text-sm">{data.messageText}</div>
            </div>

            <div className="rounded-md border p-3 space-y-2" role="status" data-testid="broadcast-job-summary">
              <div className="font-medium">Sent {counts.confirmed} of {data.recipients.length}</div>
              <div className="text-sm">
                {deadlineOverdue ? "Timed out waiting for an update — delivery unknown" : data.job ? (JOB_LABELS[data.job.state] ?? data.job.state.replaceAll("_", " ")) : "Delivery tracking"}
              </div>
              {data.job && <div className="text-xs text-muted-foreground">
                Last updated: {new Date(data.job.updatedAt).toLocaleString()}
                {data.job.deadlineAt && <div>Attempt deadline: {new Date(data.job.deadlineAt).toLocaleString()}</div>}
              </div>}
              {deadlineOverdue && <p className="text-xs text-amber-800 dark:text-amber-300">The worker has not reported a final result. Do not assume delivery failed or send a new copy manually. Refresh status before retrying.</p>}
              {!jobActive && <p className="text-xs text-muted-foreground">Processing has stopped. This record stays available; no automatic retries are running. Refresh status to check for a later delivery confirmation.</p>}
              {jobActive && !shouldPollBroadcast(data, openedAt, now) && <p className="text-xs text-muted-foreground">Automatic status updates are paused. Refresh status to check again, or cancel remaining work. Closing this view does not cancel a broadcast.</p>}
              {unknownCount > 0 && <p className="text-xs text-amber-800 dark:text-amber-300">PadSplit has not confirmed {unknownCount} recipient{unknownCount === 1 ? "" : "s"}. They may have received the message. Resending can create duplicates.</p>}
              {unknownCount > 0 && <label className="flex items-start gap-2 text-sm">
                <Checkbox checked={confirmUnknown} onCheckedChange={(v) => setConfirmUnknown(v === true)} data-testid="checkbox-broadcast-confirm-unknown" />
                I checked the unknown deliveries and accept the risk of duplicates when resending.
              </label>}
              {rosterBlocked && <div className="text-sm space-y-2" data-testid="broadcast-roster-blocked">
                <p>Nothing was sent. The resident check did not pass. Correct the resident data or connection and check again; retry cannot bypass this check.</p>
                {data.job?.error && <p>{data.job.error}</p>}
                {onReviewRoster
                  ? <Button size="sm" variant="outline" onClick={onReviewRoster} data-testid="button-broadcast-review-roster">Review roster and resubmit</Button>
                  : <p className="text-xs text-muted-foreground">Return to Message Whole House, select the house, and choose “Review residents before resubmitting” before submitting again.</p>}
              </div>}
              <div className="flex flex-wrap gap-2">
                {!rosterBlocked && retryable > 0 && <Button size="sm" variant="outline" disabled={jobMutation.isPending || query.isError || jobActive || (unknownCount > 0 && !confirmUnknown)}
                  onClick={() => jobMutation.mutate("retry")} data-testid="button-broadcast-retry-job">
                  Retry failed / timed-out{unknownCount > 0 ? " / unknown" : ""} recipients
                </Button>}
                {(jobActive || counts.queued > 0 || counts.sending > 0) && <Button size="sm" variant="outline" disabled={jobMutation.isPending || query.isError}
                  onClick={() => jobMutation.mutate("cancel")} data-testid="button-broadcast-cancel-job">Cancel remaining</Button>}
                <Button size="sm" variant="outline" onClick={onClose} data-testid="button-broadcast-status-close">Close</Button>
                {!query.isError && <Button size="sm" variant="outline" disabled={query.isFetching} onClick={() => { setOpenedAt(Date.now()); void query.refetch(); }}>Refresh status</Button>}
              </div>
              <p className="text-xs text-muted-foreground">Cancel stops pending recipients, not messages already sent. In-flight delivery may still complete.</p>
            </div>

            {data.sendTo && (
              <div className="border border-amber-300 bg-amber-50 dark:bg-amber-950/30 rounded-md p-3 space-y-2" data-testid="broadcast-send-to-missed">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  {data.sendTo.alreadySent
                    ? `${data.sendTo.memberName} is already included in this broadcast. Check their delivery state below.`
                    : data.sendTo.deselected
                      ? `${data.sendTo.memberName} was deliberately left out of this broadcast.`
                      : data.sendTo.eligible
                        ? `${data.sendTo.memberName} missed this broadcast`
                        : `${data.sendTo.memberName} is no longer a current member.`}
                </div>
                {data.sendTo.eligible && (
                  <>
                    <p className="text-xs text-muted-foreground">
                      The same message will be sent, personalized for them (name, room, house).
                    </p>
                    <Button
                      size="sm"
                      onClick={() => sendMutation.mutate(data.sendTo!.memberId)}
                      disabled={sendMutation.isPending || sendMutation.isSuccess}
                      data-testid="button-broadcast-send-to-missed"
                    >
                      {sendMutation.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}
                      Send this broadcast to {data.sendTo.memberName}
                    </Button>
                  </>
                )}
              </div>
            )}

            <div className="flex flex-wrap gap-2 text-xs" data-testid="broadcast-status-counts">
              {(Object.keys(counts) as RecipientState[])
                .filter((s) => counts[s] > 0)
                .map((s) => (
                  <span key={s} className="flex items-center gap-1"><StateBadge state={s} /> {counts[s]}</span>
                ))}
            </div>

            <div className="border rounded-md divide-y">
              {data.recipients.length === 0 && (
                <div className="p-3 text-sm text-muted-foreground">No recipients were queued.</div>
              )}
              {data.recipients.map((r) => (
                <div key={`${r.memberId}-${r.pendingId}`} className="p-2 flex items-start gap-2 text-sm" data-testid={`broadcast-recipient-${r.memberId}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">
                      {r.memberName}
                      {r.source === "roster_gap" && <span className="ml-1.5 text-xs text-muted-foreground">(added later)</span>}
                      {r.source === "auto_roster_gap" && <span className="ml-1.5 text-xs text-muted-foreground">(automatically included)</span>}
                    </div>
                    <div className="text-xs text-muted-foreground">{propName(r.propertyId)}</div>
                    {r.sendError && <div className="text-xs text-red-600 break-words">{r.sendError}</div>}
                    <div className="text-xs text-muted-foreground">Attempts: {r.attempts}{(r.confirmedAt ?? r.sentAt ?? r.updatedAt ?? r.startedAt) && ` · ${new Date((r.confirmedAt ?? r.sentAt ?? r.updatedAt ?? r.startedAt)!).toLocaleString()}`}</div>
                  </div>
                  <StateBadge state={r.state} />
                </div>
              ))}
            </div>

            {data.skipped.length > 0 && (
              <div className="text-xs space-y-1" data-testid="broadcast-status-skipped">
                <div className="font-medium">Not queued ({data.skipped.length})</div>
                {data.skipped.map((s) => (
                  <div key={s.memberId} className="text-muted-foreground">{s.memberName}: {s.error}</div>
                ))}
              </div>
            )}

            {data.rosterCheck && data.rosterCheck.gapCount > 0 && (
              <div className="text-xs space-y-1" data-testid="broadcast-status-roster-gaps">
                <div className="font-medium">
                  Roster gaps {data.acknowledgedAt ? "acknowledged at send" : "at send"} ({data.rosterCheck.gapCount})
                </div>
                {data.rosterCheck.properties.flatMap((p) => p.gaps).map((g, i) => (
                  <div key={i} className="text-muted-foreground">{g.detail}</div>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function RecentBroadcastsDialog({
  open,
  onOpenChange,
  onSelect,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (broadcastId: string) => void;
}) {
  const { toast } = useToast();
  const [openedAt, setOpenedAt] = useState(Date.now);
  useEffect(() => { if (open) setOpenedAt(Date.now()); }, [open]);
  const [hiddenIds, setHiddenIds] = useState<string[]>(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("hidden-broadcast-records") ?? "[]");
      return Array.isArray(stored) ? stored.filter((id): id is string => typeof id === "string").slice(-500) : [];
    } catch { return []; }
  });
  const [showHidden, setShowHidden] = useState(false);
  function setHidden(id: string, hidden: boolean) {
    const next = hidden ? [...new Set([...hiddenIds, id])].slice(-500) : hiddenIds.filter(value => value !== id);
    try {
      localStorage.setItem("hidden-broadcast-records", JSON.stringify(next));
      setHiddenIds(next);
    } catch {
      toast({ title: "Could not save visibility", description: "Browser storage is unavailable. The record has not been hidden.", variant: "destructive" });
    }
  }
  const query = useQuery<RecentBroadcast[]>({
    queryKey: ["/api/app/messages/broadcasts/recent"],
    queryFn: async ({ signal }) => {
      const controller = new AbortController();
      const abort = () => controller.abort();
      signal.addEventListener("abort", abort, { once: true });
      try {
        return await boundedBroadcastRequest(controller, async () => {
          const res = await apiRequest("GET", "/api/app/messages/broadcasts/recent", undefined, { signal: controller.signal });
          return res.json();
        }, 12_000);
      } finally {
        signal.removeEventListener("abort", abort);
      }
    },
    enabled: open,
    staleTime: 0,
    refetchInterval: q => open && q.state.status !== "error" && q.state.data?.some(b => shouldPollBroadcast(b, openedAt)) ? 2_000 : false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    retry: false,
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="dialog-recent-broadcasts">
        <DialogHeader>
          <DialogTitle>Recent broadcasts</DialogTitle>
          <DialogDescription>Open a broadcast to see delivery details or cancel remaining work. Finished records stop updating automatically. Hide removes a finished record from this browser’s list, not its delivery history.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" disabled={query.isFetching} onClick={() => { setOpenedAt(Date.now()); void query.refetch(); }}>Refresh list</Button>
          <Button size="sm" variant="outline" onClick={() => setShowHidden(value => !value)}>{showHidden ? "Show recent" : "Show hidden"}</Button>
        </div>
        <p className="text-xs text-muted-foreground">Only active broadcasts update automatically, for up to 90 seconds per visit. Refresh list checks again without resending any messages.</p>
        {query.isError ? (
          <div className="text-sm text-red-700">{parseErrorMessage(query.error, "Couldn't load recent broadcasts.")}<Button variant="outline" size="sm" onClick={() => void query.refetch()}>Try again</Button></div>
        ) : !query.data ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : query.data.length === 0 ? (
          <div className="text-sm text-muted-foreground">No broadcasts yet.</div>
        ) : (
          <div className="border rounded-md divide-y">
            {query.data.filter(b => showHidden ? hiddenIds.includes(b.id) : !hiddenIds.includes(b.id)).map((b) => {
              const unconfirmed = (b.counts.queued ?? 0) + (b.counts.handed_off ?? 0) + (b.counts.sending ?? 0);
              return (
                <div key={b.id}>
                <button
                  type="button"
                  className="w-full text-left p-2 hover:bg-muted/50 space-y-1"
                  onClick={() => onSelect(b.id)}
                  data-testid={`row-recent-broadcast-${b.id}`}
                >
                  <div className="flex items-center justify-between gap-2 text-sm">
                    <span className="font-medium truncate">{b.propertyNames.join(", ")}</span>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {new Date(b.scheduledFor ?? b.createdAt).toLocaleString()}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{b.messagePreview}</div>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <span>Sent {b.counts.confirmed ?? 0} of {b.recipientCount}</span>
                    {b.job && <span>{JOB_LABELS[b.job.state] ?? b.job.state.replaceAll("_", " ")}</span>}
                    {unconfirmed > 0 && <span className="text-blue-700 dark:text-blue-300">{unconfirmed} unconfirmed</span>}
                    {(b.counts.failed ?? 0) > 0 && <span className="text-red-700 dark:text-red-400">{b.counts.failed} failed</span>}
                    {(b.counts.timed_out ?? 0) > 0 && <span className="text-amber-700 dark:text-amber-300">{b.counts.timed_out} timed out</span>}
                    {(b.counts.unknown ?? 0) > 0 && <span className="text-amber-700 dark:text-amber-300">{b.counts.unknown} delivery unknown</span>}
                    {b.gapCount > 0 && <span className="text-amber-700 dark:text-amber-300">{b.gapCount} roster gap{b.gapCount === 1 ? "" : "s"} acknowledged</span>}
                  </div>
                </button>
                {!broadcastIsActive(b) && <Button size="sm" variant="ghost" className="mb-2 ml-2" onClick={() => setHidden(b.id, !hiddenIds.includes(b.id))}>
                  {hiddenIds.includes(b.id) ? "Restore to recent" : "Hide from recent"}
                </Button>}
                </div>
              );
            })}
            {!query.data.some(b => showHidden ? hiddenIds.includes(b.id) : !hiddenIds.includes(b.id)) && <p className="p-3 text-sm text-muted-foreground">{showHidden ? "No hidden broadcasts." : "No visible broadcasts. Use Show hidden to restore a record."}</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Opens the status view from a Team Inbox deep link:
 * `/app/messages?broadcast=<id>` (delivery-incomplete) or
 * `...&sendTo=<memberId>` (roster gap — pre-filled one-click send).
 * wouter's location omits the query, so state is seeded from
 * window.location.search once and the params are stripped on close.
 */
export function BroadcastDeepLinkDialog() {
  const [target, setTarget] = useState<{ id: string; sendTo: number | null } | null>(() => {
    if (typeof window === "undefined") return null;
    const params = new URLSearchParams(window.location.search);
    const id = params.get("broadcast");
    if (!id) return null;
    const sendTo = Number(params.get("sendTo"));
    return { id, sendTo: Number.isInteger(sendTo) && sendTo > 0 ? sendTo : null };
  });
  return (
    <BroadcastStatusDialog
      broadcastId={target?.id ?? null}
      sendToMemberId={target?.sendTo ?? null}
      onClose={() => {
        setTarget(null);
        const params = new URLSearchParams(window.location.search);
        params.delete("broadcast");
        params.delete("sendTo");
        const qs = params.toString();
        window.history.replaceState(window.history.state, "", `${window.location.pathname}${qs ? `?${qs}` : ""}`);
      }}
    />
  );
}
